import React from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useLicense } from '../../hooks/useLicense';
import { useTenant } from '../../contexts/TenantContext';

/**
 * ScaleGate — reusable "visible-but-locked" wrapper for paid-tier features.
 *
 * Renders children as-is if the current tenant meets the minTier requirement
 * (default: Scale) or the user is a superadmin. Otherwise:
 *   - dims the children with reduced opacity + pointer-events:none so the
 *     feature remains visible and demoable but cannot be interacted with
 *   - overlays a small lock chip (bottom-right by default) that hovers/
 *     focuses to reveal an inline "Available on {Tier}" tooltip
 *
 * Deliberately does NOT render an upgrade CTA that links anywhere yet —
 * the payment upgrade flow does not exist. Tooltip surfaces the ask; sales
 * closes it manually until we build a self-serve upgrade route.
 *
 * Usage:
 *   <ScaleGate feature="CRM webhook push">
 *     <MyExpensiveInputsAndSaveButton />
 *   </ScaleGate>
 *
 *   <ScaleGate feature="WhatsApp handoff" minTier="growth">
 *     <MyGrowthPlusInputs />
 *   </ScaleGate>
 *
 * Props:
 *   feature (string)     — human-readable feature name for the tooltip
 *   className (string)   — extra classes on the wrapper
 *   overrideOpen (bool)  — force-render unlocked (for previews / demos)
 *   minTier (string)     — 'scale' (default) or 'growth'. Growth-min unlocks
 *                          for Growth AND Scale tenants; Scale-min only for
 *                          Scale.
 */
const ScaleGate = ({ children, feature, className = '', overrideOpen = false, minTier = 'scale', entitlement, addonLabel }) => {
  const { isScale, isGrowthOrScale, hasFeature, isLoading } = useLicense();
  const { isSuperadmin } = useTenant();

  // An add-on feature unlocks on its entitlement (the add-on on any plan, or
  // Scale, which includes it); otherwise the plan decides.
  const meetsMinTier = entitlement ? hasFeature(entitlement) : (minTier === 'growth' ? isGrowthOrScale : isScale);
  const tierLabel = entitlement ? (addonLabel || 'Add-on') : (minTier === 'growth' ? 'Growth' : 'Scale');
  const availability = entitlement
    ? `the ${tierLabel} add-on (included in Scale)`
    : `the ${tierLabel} plan`;

  // While the license fetch is in flight we render children unlocked to
  // avoid a jarring lock->unlock flicker for paid tenants. Superadmins
  // always bypass — they impersonate tenants for QA / support.
  const unlocked = overrideOpen || isSuperadmin || meetsMinTier || isLoading;

  if (unlocked) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`relative group ${className}`}>
      <div
        aria-disabled="true"
        className="pointer-events-none select-none opacity-50 grayscale"
      >
        {children}
      </div>
      <div className="absolute top-2 right-2 z-10">
        <div
          tabIndex={0}
          role="note"
          aria-label={`${feature || 'This feature'} is available with ${availability}`}
          className="flex items-center gap-1 rounded-full bg-gray-900 text-white text-xs font-semibold px-2.5 py-1 shadow-md cursor-help focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <LockClosedIcon className="w-3 h-3" />
          <span>{tierLabel}</span>
        </div>
        <div className="hidden group-hover:block group-focus-within:block absolute right-0 mt-2 w-64 bg-gray-900 text-white text-xs rounded-lg shadow-lg px-3 py-2 z-20">
          <p className="font-semibold mb-1">Available with {availability}</p>
          <p className="opacity-90 leading-snug">
            {feature ? `${feature} is part of ${availability}.` : `This feature is part of ${availability}.`} Add it from Billing or talk to us.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScaleGate;
